import React, { useState } from 'react';
import { X, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function ImageUploadModal({ delivery, onClose, onUploadComplete }) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  }

  function processImageFile(file) {
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImageFile(dataUrl);
      setImagePreview(dataUrl);
      setError(null);
    };
    reader.readAsDataURL(file);
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          processImageFile(file);
        }
        break;
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processImageFile(file);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function removeImage() {
    setImageFile(null);
    setImagePreview(null);
    setError(null);
  }

  async function handleUpload() {
    if (!imageFile) {
      setError('Por favor selecciona una imagen');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/provincia-deliveries/${delivery.id}/images`,
        { image: imageFile }
      );

      onUploadComplete(response.data.image.image_url);
      onClose();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.error || 'Error al subir la imagen. Por favor intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                Subir Imagen
              </h3>
              <p className="text-sm text-slate-600 font-montserrat mt-1">
                Entrega #{delivery.id} - {delivery.recipient_name}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={uploading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm font-montserrat">
                {error}
              </div>
            )}

            {!imagePreview ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onPaste={handlePaste}
                className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center hover:border-brand-gold hover:bg-brand-gold/5 transition-all cursor-pointer"
              >
                <ImageIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-sm text-slate-600 font-montserrat mb-2">
                  Arrastra una imagen aquí o pega desde el portapapeles
                </p>
                <p className="text-xs text-slate-500 font-montserrat mb-6">
                  También puedes hacer Ctrl+V para pegar
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="bg-brand-gold hover:bg-brand-gold/90 text-white px-6 py-3 rounded-lg text-sm font-montserrat font-semibold cursor-pointer inline-flex items-center gap-2 transition-colors shadow-md">
                    <Upload className="w-4 h-4" />
                    Seleccionar archivo
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-64 object-contain rounded-xl border-2 border-slate-200"
                />
                <button
                  onClick={removeImage}
                  disabled={uploading}
                  className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-white transition-all text-sm font-medium text-slate-700 disabled:opacity-50 font-montserrat"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={!imageFile || uploading}
              className="px-5 py-2.5 rounded-xl bg-brand-gold hover:bg-brand-gold/90 text-white transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md font-montserrat"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Subir Imagen
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ImageUploadModal;